import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface ProfileDropdownProps {
  onProfileClick: () => void;
}

export const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ onProfileClick }) => {
  const { user, profile, signOut, resetAuthState } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setIsOpen(false);
  };

  const handleProfileClick = () => {
    onProfileClick();
    setIsOpen(false);
  };

  const handleResetAuth = async () => {
    setIsOpen(false);
    try {
      await resetAuthState();
    } catch (error) {
      console.error('Error during auth reset:', error);
    }
  };



  const getInitials = () => {
    if (profile?.full_name) {
      return profile.full_name
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (profile?.username) {
      return profile.username.slice(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Picture Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-10 h-10 rounded-full overflow-hidden border-2 border-gray-600 hover:border-gray-400 transition-colors focus:outline-none focus:border-blue-500"
      >
        {profile?.avatar_url ? (
          <img
            src={profile.avatar_url}
            alt="Profile"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
            {getInitials()}
          </div>
        )}

      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-gray-900 border-2 border-gray-700 rounded-lg shadow-lg z-50">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-600">
                {profile?.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs">
                    {getInitials()}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">
                  {profile?.full_name || profile?.username || 'User'}
                </p>
                <p className="text-gray-400 text-sm truncate">
                  {user.email}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            <button
              onClick={handleProfileClick}
              className="w-full px-4 py-2 text-left text-gray-300 hover:bg-gray-800 hover:text-white transition-colors flex items-center gap-3"
            >
              <img 
                src="https://unpkg.com/pixelarticons@1.8.0/svg/user.svg" 
                alt="Profile" 
                className="w-4 h-4"
                style={{ filter: `brightness(0) saturate(100%) invert(60%)` }}
              />
              Profile Settings
            </button>

            <button
              onClick={handleResetAuth}
              className="w-full px-4 py-2 text-left text-yellow-400 hover:bg-gray-800 hover:text-yellow-300 transition-colors flex items-center gap-3"
              title="Reset authentication state if you're experiencing issues"
            >
              <img 
                src="https://unpkg.com/pixelarticons@1.8.0/svg/refresh.svg" 
                alt="Reset Auth" 
                className="w-4 h-4"
                style={{ filter: `brightness(0) saturate(100%) invert(85%) sepia(43%) saturate(1352%) hue-rotate(359deg) brightness(99%) contrast(93%)` }}
              />
              Reset Auth
            </button>

            <div className="border-t border-gray-700 my-2"></div>

            <button
              onClick={handleSignOut}
              className="w-full px-4 py-2 text-left text-red-400 hover:bg-gray-800 hover:text-red-300 transition-colors flex items-center gap-3"
            >
              <img 
                src="https://unpkg.com/pixelarticons@1.8.0/svg/logout.svg" 
                alt="Sign Out" 
                className="w-4 h-4"
                style={{ filter: `brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(316deg) brightness(95%) contrast(93%)` }}
              />
              Sign Out
            </button>
          </div>
        </div>
      )}


    </div>
  );
}; 